import { BusyPeriodFormValues, COMMENT_MAX_LENGTH, SupplierBusyPeriod } from "pages/out-of-office/out-of-office.typing";
import { useBusyPeriodOverlapQuery } from "query/busy-period.query";
import { format, isBefore, startOfDay, startOfMonth, startOfToday } from "date-fns";
import { ChangeEvent, FormEvent, useMemo, useState } from "react";
import { DateRange, Matcher } from "react-day-picker";
import { useCurrentLanguage } from "utils/language";

const toFormValues = (period?: SupplierBusyPeriod): BusyPeriodFormValues => ({
  startDate: period?.startDate,
  endDate: period?.endDate,
  canAcceptSmallAssignments: period?.canAcceptSmallAssignments ?? false,
  comments: period?.comments ?? "",
});

/** Prefer the month where the selected range is visible; fall back to current month. */
const getInitialCalendarMonth = (period?: SupplierBusyPeriod) => {
  if (!period?.startDate) {
    return startOfMonth(new Date());
  }
  return startOfMonth(period.startDate);
};

const getEarliestNavigableMonth = (period?: SupplierBusyPeriod) => {
  const currentMonth = startOfMonth(new Date());
  if (!period?.startDate) {
    return currentMonth;
  }
  const rangeStartMonth = startOfMonth(period.startDate);
  return rangeStartMonth < currentMonth ? rangeStartMonth : currentMonth;
};

const getMinSelectableDate = (period: SupplierBusyPeriod | undefined, today: Date) => {
  if (period?.startDate && isBefore(startOfDay(period.startDate), today)) {
    return startOfDay(period.startDate);
  }
  return today;
};

export const useOutOfOfficeFormView = (
  initialPeriod: SupplierBusyPeriod | undefined,
  onSubmit: (values: BusyPeriodFormValues) => void
) => {
  const { locale } = useCurrentLanguage();
  const [earliestMonth] = useState(() => getEarliestNavigableMonth(initialPeriod));
  const [minSelectableDate] = useState(() => getMinSelectableDate(initialPeriod, startOfToday()));
  const [today] = useState(() => startOfToday());
  const [displayedMonth, setDisplayedMonth] = useState(() => getInitialCalendarMonth(initialPeriod));
  const [values, setValues] = useState<BusyPeriodFormValues>(() => toFormValues(initialPeriod));
  const [validationError, setValidationError] = useState<string | null>(null);

  const dateRange: DateRange | undefined =
    values.startDate || values.endDate ? { from: values.startDate, to: values.endDate } : undefined;

  const disabledDays: Matcher = { before: minSelectableDate };

  const { data: overlappingDates = [], isFetching: isCheckingOverlap } = useBusyPeriodOverlapQuery(
    values.startDate,
    values.endDate,
    initialPeriod?.id
  );

  const overlapWarningDays = useMemo(
    () => (overlappingDates.length ? overlappingDates.map((date) => format(date, "PP", { locale })).join(", ") : null),
    [overlappingDates, locale]
  );

  const handleDateSelect = (range: DateRange | undefined) => {
    setValidationError(null);
    setValues((prev) => ({
      ...prev,
      startDate: range?.from,
      endDate: range?.to ?? range?.from,
    }));
  };

  const handleCanAcceptSmallAssignmentsChange = (checked: boolean) => {
    setValues((prev) => ({ ...prev, canAcceptSmallAssignments: checked }));
  };

  const handleCommentChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setValidationError(null);
    setValues((prev) => ({ ...prev, comments: event.target.value }));
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();

    if (!values.startDate || !values.endDate) {
      setValidationError("outOfOffice.form.validation.datesRequired");
      return;
    }

    // List view only shows upcoming & current (end date >= today)
    if (isBefore(startOfDay(values.endDate), today)) {
      setValidationError("outOfOffice.form.validation.datesInPast");
      return;
    }

    if (values.comments.length > COMMENT_MAX_LENGTH) {
      setValidationError("outOfOffice.form.validation.commentTooLong");
      return;
    }

    onSubmit({
      ...values,
      comments: values.comments.trim(),
    });
  };

  return {
    earliestMonth,
    minSelectableDate,
    disabledDays,
    displayedMonth,
    setDisplayedMonth,
    values,
    validationError,
    overlapWarningDays,
    isCheckingOverlap,
    dateRange,
    commentMaxLength: COMMENT_MAX_LENGTH,
    handleDateSelect,
    handleCanAcceptSmallAssignmentsChange,
    handleCommentChange,
    handleSubmit,
  };
};
